import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { RootLayout } from './RootLayout'
import { Login } from './pages/Login'
import { AuthProvider, RequireAuth } from './AuthProvider'
import { GqlProvider } from './GqlProvider'
import { ResetPassword } from './pages/ResetPassword'
import { NotFound } from './pages/Notfound'
import { Toaster } from 'sonner'
import { environment } from './environment'

import * as Sentry from '@sentry/react'
import { Dashboard } from './pages/Dashboard'
import { Group } from './pages/Group'
import { Company } from './pages/Company'
import { User } from './pages/User'
import { UserSettings } from './pages/UserSettings'
import { GroupOwner } from './pages/GroupOwner'
import { SuperAdmin } from './pages/SuperAdmin'
import { ComplianceOfficer } from './pages/ComplianceOfficer'
import { DepositTransaction } from './pages/DepositTransaction'
import { SwapTransaction } from './pages/SwapTransaction'
import { ManageGroup } from './pages/ManageGroup'
import { ManageCompany } from './pages/ManageCompany'
import { ErrorBoundary, LogoProvider, RouterErrorBoundary } from '@valiant/ui-shared'
import { OrgGraphPage } from './pages/OrgGraph'
import { Maintenance } from './pages/Maintenance'

Sentry.init({
  dsn: environment.SENTRY_DSN,
  sendDefaultPii: true,
  integrations: [Sentry.browserTracingIntegration()],
  tracePropagationTargets: [environment.API_URL],
  tracesSampleRate: environment.SENTRY_TRACES_SAMPLE_RATE,
})

// Dynamically set theme main color from env, if provided
const mainColor = environment.MAIN_COLOR as string | undefined
if (mainColor && typeof mainColor === 'string' && mainColor.trim()) {
  document.documentElement.style.setProperty('--main', mainColor.trim())
}
// Dynamically set theme destructive color from env, if provided
const destructiveColor = environment.DESTRUCTIVE_COLOR as string | undefined
if (destructiveColor && typeof destructiveColor === 'string' && destructiveColor.trim()) {
  document.documentElement.style.setProperty('--destructive', destructiveColor.trim())
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { path: '/login', element: <Login />, errorElement: <RouterErrorBoundary /> },
      {
        path: '/reset-password/:token',
        element: <ResetPassword />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/',
        element: (
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/groups/:groupId',
        element: (
          <RequireAuth>
            <Group />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/groups/:groupId/manage',
        element: (
          <RequireAuth>
            <ManageGroup />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/companies/:companyId',
        element: (
          <RequireAuth>
            <Company />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/companies/:companyId/manage',
        element: (
          <RequireAuth>
            <ManageCompany />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/users/:userId',
        element: (
          <RequireAuth>
            <User />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/users/:userId/settings',
        element: (
          <RequireAuth>
            <UserSettings />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/group-owner/:userId',
        element: (
          <RequireAuth>
            <GroupOwner />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/super-admin/:userId',
        element: (
          <RequireAuth>
            <SuperAdmin />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/compliance-officer/:userId',
        element: (
          <RequireAuth>
            <ComplianceOfficer />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/transactions/deposit/:depositId',
        element: (
          <RequireAuth>
            <DepositTransaction />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/transactions/swap/:transactionId',
        element: (
          <RequireAuth>
            <SwapTransaction />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/org-graph',
        element: (
          <RequireAuth>
            <OrgGraphPage />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/maintenance',
        element: (
          <RequireAuth>
            <Maintenance />
          </RequireAuth>
        ),
        errorElement: <RouterErrorBoundary />,
      },
      { path: '*', element: <NotFound />, errorElement: <RouterErrorBoundary /> },
    ],
  },
])

const msb = environment.MSB_LOGO || `/${environment.MSB}.svg` || '/local.svg'
const favicon = document.getElementById('dynamic-favicon') as HTMLLinkElement | null
if (favicon) {
  favicon.href = msb
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <LogoProvider
      msb={environment.MSB}
      msbLogo={environment.MSB_LOGO}
      msbLogoLoading={environment.MSB_LOGO_LOADING}
    >
      <ErrorBoundary>
        <AuthProvider>
          <GqlProvider>
            <RouterProvider router={router} />
            <Toaster />
          </GqlProvider>
        </AuthProvider>
      </ErrorBoundary>
    </LogoProvider>
  </StrictMode>,
)
